/**
 * Settings sync service — subscribes to settingsStore and appStore changes
 * and debounce-saves to the backend via PUT /api/settings.
 *
 * Call `startSettingsSync()` once after loading initial settings from the backend.
 * Call `stopSettingsSync()` on logout to prevent stale writes.
 */

import { apiClient } from './apiClient'
import { useSettingsStore } from '@/stores/settingsStore'
import { useAppStore } from '@/stores/appStore'

let syncTimeout: ReturnType<typeof setTimeout> | null = null
let unsubSettings: (() => void) | null = null
let unsubApp: (() => void) | null = null
let pendingSave = false

const DEBOUNCE_MS = 1500

/**
 * Immediately execute pending save. Called when page loses visibility.
 */
async function flushPendingSave() {
  if (syncTimeout && pendingSave) {
    clearTimeout(syncTimeout)
    syncTimeout = null
    try {
      const payload = getFullSettingsPayload()
      console.debug('[SettingsSync] Flushing pending save on visibility change')
      await apiClient.fetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify({ settings: payload }),
      })
      console.debug('[SettingsSync] Flushed save completed')
      pendingSave = false
    } catch (error) {
      console.warn('[SettingsSync] Flush save failed:', error)
    }
  }
}

/**
 * Handle visibility change - flush saves when page is hidden.
 */
function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    flushPendingSave()
  }
}

function getFullSettingsPayload() {
  const settingsData = useSettingsStore.getState().getSettingsForSync()
  const preferences = useAppStore.getState().getPreferencesForSync()
  return { ...settingsData, preferences }
}

function scheduleSave() {
  // Save only settings read from the server (SA-1301): after a failed read the store holds the defaults, and saving
  // them would replace every stored section.
  if (!useSettingsStore.getState()._settingsSaveable) {
    console.debug('[SettingsSync] Skipping save - settings not saveable yet')
    return
  }

  if (syncTimeout) clearTimeout(syncTimeout)
  pendingSave = true
  syncTimeout = setTimeout(async () => {
    try {
      const payload = getFullSettingsPayload()
      console.debug('[SettingsSync] Saving payload:', JSON.stringify(payload.voiceFeatures, null, 2))
      await apiClient.fetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify({ settings: payload }),
      })
      console.debug('[SettingsSync] Saved to backend')
      pendingSave = false
    } catch (error) {
      console.warn('[SettingsSync] Failed to save:', error)
      pendingSave = false
    }
  }, DEBOUNCE_MS)
}

/**
 * Start watching store changes and syncing to backend.
 * Call after initial settings load from backend completes.
 */
export function startSettingsSync() {
  // Clean up any existing subscriptions
  stopSettingsSync()

  // Add visibility change handler to flush pending saves when page is hidden
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // Subscribe to settingsStore changes (connections, voiceFeatures, voiceConversation, ui)
  unsubSettings = useSettingsStore.subscribe(
    (state, prevState) => {
      // Only trigger on synced fields, ignore runtime-only changes (ragApiKey, has*Key flags)
      const voiceFeaturesChanged = state.voiceFeatures !== prevState.voiceFeatures
      if (voiceFeaturesChanged) {
        console.debug('[SettingsSync] voiceFeatures changed:', {
          old: prevState.voiceFeatures.useStreamingTts,
          new: state.voiceFeatures.useStreamingTts,
        })
      }
      if (
        state.connections.voiceProvider !== prevState.connections.voiceProvider ||
        state.connections.avatarProvider !== prevState.connections.avatarProvider ||
        voiceFeaturesChanged ||
        state.voiceConversation !== prevState.voiceConversation ||
        state.ui !== prevState.ui
      ) {
        scheduleSave()
      }
    }
  )

  // Subscribe to appStore changes (theme, activePersonaId, isSidebarCollapsed)
  unsubApp = useAppStore.subscribe(
    (state, prevState) => {
      if (
        state.theme !== prevState.theme ||
        state.activePersonaId !== prevState.activePersonaId ||
        state.isSidebarCollapsed !== prevState.isSidebarCollapsed
      ) {
        scheduleSave()
      }
    }
  )

  console.debug('[SettingsSync] Started')
}

/**
 * Stop watching store changes. Call on logout.
 */
export function stopSettingsSync() {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (syncTimeout) {
    clearTimeout(syncTimeout)
    syncTimeout = null
  }
  pendingSave = false
  if (unsubSettings) {
    unsubSettings()
    unsubSettings = null
  }
  if (unsubApp) {
    unsubApp()
    unsubApp = null
  }
}
